import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import { apiPostAuth } from "../../utils/api";

export default function BillingPayment() {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [hasPendingPayment, setHasPendingPayment] = useState(false);
  const [pendingPaymentLink, setPendingPaymentLink] = useState("");
  const [availableCredits, setAvailableCredits] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await apiPostAuth("google_apps/payment_init.php");

        if (response.result === 1) {
          setProducts(response.product_list || []);
          setHasPendingPayment(response.has_pending_payment === 1);
          setPendingPaymentLink(response.pending_payment_link || "");
          setAvailableCredits(response.google_play_credit || 0);
        } else {
          alert(response.message || "Failed to load payment data.");
        }
      } catch (error) {
        alert("Unable to load payment data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const handleCheckout = () => {
    if (!selectedProductId) return;
    navigate(`/dashboard/billing-payment/${selectedProductId}/checkout`);
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <div className="loading-spinner"></div>
        <p>Loading available credits...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <h1 style={{ fontSize: "1.8rem", marginBottom: "10px", color: "#1f2937" }}>
        Billing & Payments
      </h1>
      <p style={{ fontSize: "1rem", marginBottom: "20px", color: "#4b5563" }}>
        Choose the credit package you want to purchase.
      </p>

      {/* Available Credits Section */}
      <div style={creditInfoStyle}>
        <h2 style={{ fontSize: "1.2rem", color: "#1f2937" }}>Available Credits</h2>
        <p style={{ fontSize: "1.5rem", fontWeight: "700", color: "#2563eb" }}>
          {availableCredits}
        </p>
      </div>

      {/* Pending Payment Section */}
      {hasPendingPayment && (
        <div style={pendingSectionStyle}>
          <h3 style={{ color: "#b91c1c", marginBottom: "10px" }}>
            You have an unpaid purchase
          </h3>
          <button
            className="primary-button"
            onClick={() => window.open(pendingPaymentLink, "_blank")}
            style={{ marginBottom: "20px" }}
          >
            Complete Payment
          </button>
        </div>
      )}

      {/* Product Selection Grid */}
      <div style={gridContainerStyle}>
        {products.map((product) => (
          <div
            key={product.product_id}
            onClick={() =>
              !hasPendingPayment && setSelectedProductId(product.product_id)
            }
            style={{
              ...cardStyle,
              border:
                selectedProductId === product.product_id
                  ? "2px solid #2563eb"
                  : "1px solid #ddd",
              opacity: hasPendingPayment ? 0.5 : 1,
              cursor: hasPendingPayment ? "not-allowed" : "pointer",
            }}
          >
            {product.product_discount_label > 0 && (
              <span style={discountBadgeStyle}>
                {product.product_discount_label}% OFF
              </span>
            )}
            <h3 style={creditTitleStyle}>{product.product_name}</h3>
            <p style={creditSubtitleStyle}>{product.credit_count} Credits</p>
            <p style={priceUSDStyle}>${product.product_price_usd.toLocaleString()}</p>
            <p style={priceIDRStyle}>
              ≈ IDR {product.product_price.toLocaleString()}
            </p>
          </div>
        ))}
      </div>

      {/* Checkout Button */}
      <button
        className="primary-button"
        onClick={handleCheckout}
        disabled={!selectedProductId || hasPendingPayment}
        style={{
          marginTop: "20px",
          opacity: !hasPendingPayment && selectedProductId ? 1 : 0.6,
          cursor:
            !hasPendingPayment && selectedProductId ? "pointer" : "not-allowed",
        }}
      >
        Checkout
      </button>
    </div>
  );
}

const creditInfoStyle = {
  backgroundColor: "#f9fafb",
  padding: "15px",
  borderRadius: "8px",
  marginBottom: "20px",
  border: "1px solid #e5e7eb",
  textAlign: "center",
};

const pendingSectionStyle = {
  backgroundColor: "#fff3f3",
  padding: "15px",
  border: "1px solid #fca5a5",
  borderRadius: "6px",
  marginBottom: "20px",
  textAlign: "center",
};

const gridContainerStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "20px",
};

const cardStyle = {
  backgroundColor: "#fff",
  borderRadius: "8px",
  padding: "20px",
  textAlign: "center",
  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
  transition: "border 0.2s ease",
  position: "relative",
};

const creditTitleStyle = {
  fontSize: "1.2rem",
  fontWeight: "600",
  color: "#1f2937",
  marginBottom: "4px",
};

const creditSubtitleStyle = {
  fontSize: "0.95rem",
  color: "#4b5563",
  marginBottom: "10px",
};

const priceUSDStyle = {
  fontSize: "1.4rem",
  fontWeight: "700",
  color: "#2563eb",
  marginBottom: "5px",
};

const priceIDRStyle = {
  fontSize: "0.9rem",
  color: "#6b7280",
};

const discountBadgeStyle = {
  position: "absolute",
  top: "10px",
  right: "10px",
  backgroundColor: "#e53e3e",
  color: "white",
  padding: "4px 8px",
  borderRadius: "4px",
  fontSize: "0.8rem",
  fontWeight: "bold",
};
